import { useState, useCallback, useRef } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import Spinner from "@excalidraw/excalidraw/components/Spinner";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { t } from "@excalidraw/excalidraw/i18n";
import { abysiusApi } from "../data/abysiusApi";
import { useAtom, atom } from "../app-jotai";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./PromptToImage.scss";

// Atoms for dialog state
export const promptToImageDialogOpenAtom = atom(false);

interface PromptToImageDialogProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

type ImageSize = "256x256" | "512x512" | "1024x1024" | "1792x1024" | "1024x1792";
type ImageStyle = "vivid" | "natural";
type ImageQuality = "standard" | "hd";

interface GeneratedImage {
  url?: string;
  b64_json?: string;
  revised_prompt?: string;
}

export const PromptToImageDialog = ({
  excalidrawAPI,
}: PromptToImageDialogProps) => {
  const [isOpen, setIsOpen] = useAtom(promptToImageDialogOpenAtom);
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );

  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imageSize, setImageSize] = useState<ImageSize>("1024x1024");
  const [imageStyle, setImageStyle] = useState<ImageStyle>("vivid");
  const [imageQuality, setImageQuality] = useState<ImageQuality>("standard");
  const [numberOfImages, setNumberOfImages] = useState(1);

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsOpen(false);
    setError(null);
    setGeneratedImages([]);
    setSelectedImageIndex(null);
  }, [setIsOpen]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating) {
      return;
    }

    if (!abysiusApi.isConfigured()) {
      setError("Abysius API key not configured. Please set up your API key.");
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImages([]);
    setSelectedImageIndex(null);

    trackEvent("ai", "generate", "prompt-to-image");

    try {
      abortControllerRef.current = new AbortController();

      // Call DALL-E image generation
      const images = await abysiusApi.generateImageFromPrompt(prompt, {
        model: "dall-e-3",
        size: imageSize,
        quality: imageQuality,
        style: imageStyle,
        n: numberOfImages,
      });

      // Set generated images with base64 data
      setGeneratedImages(
        images.map((img) => ({
          url: img.url,
          b64_json: img.b64_json,
          revised_prompt: img.revised_prompt,
        })),
      );

      trackEvent("ai", "generate success", "prompt-to-image");
    } catch (err: any) {
      console.error("Image generation error:", err);
      setError(err.message || "Failed to generate image");
      trackEvent("ai", "generate failed", "prompt-to-image");
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  }, [prompt, isGenerating, imageSize, imageStyle, imageQuality]);

  const handleInsertImage = useCallback(
    async (image: GeneratedImage) => {
      console.log("[SpryBoard] Inserting image:", { 
        hasUrl: !!image.url, 
        hasB64: !!image.b64_json,
        b64Length: image.b64_json?.length 
      });
      
      if (!image.url && !image.b64_json) {
        setError("No image data available to insert.");
        return;
      }

      try {
        let dataUrl: string;
        let mimeType = "image/png";

        if (image.b64_json) {
          // Use base64 directly - no CORS issues!
          console.log("[SpryBoard] Using base64 data directly");
          dataUrl = `data:image/png;base64,${image.b64_json}`;
        } else if (image.url) {
          // Fallback: try to fetch URL (may fail due to CORS)
          console.log("[SpryBoard] Falling back to URL fetch:", image.url);
          const response = await fetch(image.url);
          const blob = await response.blob();
          mimeType = blob.type || "image/png";
          
          dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } else {
          setError("No image data available.");
          return;
        }

        // Create image element to get dimensions
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = dataUrl;
        });

        // Generate unique file ID
        const fileId = `ai-generated-${Date.now()}` as import("@excalidraw/element/types").FileId;

        // Add the file to excalidraw's file storage
        excalidrawAPI.addFiles([{
          id: fileId,
          mimeType: mimeType as "image/png" | "image/jpeg" | "image/gif" | "image/webp" | "image/svg+xml" | "image/avif" | "image/jxl",
          dataURL: dataUrl as import("@excalidraw/excalidraw/types").DataURL,
          created: Date.now(),
        }]);

        // Get the center of the viewport
        const appState = excalidrawAPI.getAppState();
        const viewportCenterX = -appState.scrollX + appState.width / 2 / appState.zoom.value;
        const viewportCenterY = -appState.scrollY + appState.height / 2 / appState.zoom.value;

        // Scale image to reasonable size (max 800px)
        const maxSize = 800;
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const width = img.width * scale;
        const height = img.height * scale;

        // Create an image element
        const imageElement = {
          type: "image" as const,
          id: `image-${Date.now()}`,
          x: viewportCenterX - width / 2,
          y: viewportCenterY - height / 2,
          width,
          height,
          fileId,
          status: "saved" as const,
          scale: [1, 1] as [number, number],
          angle: 0,
          strokeColor: "transparent",
          backgroundColor: "transparent",
          fillStyle: "solid" as const,
          strokeWidth: 1,
          strokeStyle: "solid" as const,
          roughness: 0,
          opacity: 100,
          roundness: null,
          seed: Math.floor(Math.random() * 100000),
          version: 1,
          versionNonce: Math.floor(Math.random() * 100000),
          isDeleted: false,
          groupIds: [],
          frameId: null,
          boundElements: null,
          updated: Date.now(),
          link: null,
          locked: false,
        };

        // Add the image element to the scene
        const currentElements = excalidrawAPI.getSceneElements();
        excalidrawAPI.updateScene({
          elements: [...currentElements, imageElement],
        });

        trackEvent("ai", "insert image", "prompt-to-image");
        handleClose();
      } catch (err: any) {
        console.error("Failed to insert image:", err);
        setError(`Failed to insert image: ${err.message}`);
      }
    },
    [excalidrawAPI, handleClose],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      className="prompt-to-image-dialog"
      onCloseRequest={handleClose}
      title="Prompt to Image (AI)"
      size={800}
    >
      <div className="prompt-to-image-content">
        <div className="prompt-section">
          <label htmlFor="image-prompt">Describe the image you want:</label>
          <textarea
            id="image-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A serene mountain landscape at sunset with a lake reflection..."
            rows={4}
            disabled={isGenerating}
          />
        </div>

        <button
          className="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
          type="button"
        >
          {showAdvanced ? "▼ Hide Options" : "▶ Advanced Options"}
        </button>

        {showAdvanced && (
          <div className="advanced-options">
            <div className="option-group">
              <label>Size:</label>
              <select
                value={imageSize}
                onChange={(e) => setImageSize(e.target.value as ImageSize)}
                disabled={isGenerating}
              >
                <option value="256x256">256×256</option>
                <option value="512x512">512×512</option>
                <option value="1024x1024">1024×1024</option>
                <option value="1792x1024">1792×1024 (Landscape)</option>
                <option value="1024x1792">1024×1792 (Portrait)</option>
              </select>
            </div>

            <div className="option-group">
              <label>Style:</label>
              <select
                value={imageStyle}
                onChange={(e) => setImageStyle(e.target.value as ImageStyle)}
                disabled={isGenerating}
              >
                <option value="vivid">Vivid</option>
                <option value="natural">Natural</option>
              </select>
            </div>

            <div className="option-group">
              <label>Quality:</label>
              <select
                value={imageQuality}
                onChange={(e) =>
                  setImageQuality(e.target.value as ImageQuality)
                }
                disabled={isGenerating}
              >
                <option value="standard">Standard</option>
                <option value="hd">HD</option>
              </select>
            </div>

            <div className="option-group">
              <label>Number of images:</label>
              <select
                value={numberOfImages}
                onChange={(e) => setNumberOfImages(parseInt(e.target.value))}
                disabled={isGenerating}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="4">4</option>
              </select>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="actions">
          <FilledButton
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            label={isGenerating ? "Generating..." : "Generate Image"}
            icon={isGenerating ? <Spinner /> : undefined}
          />
        </div>

        {generatedImages.length > 0 && (
          <div className="generated-images">
            <h4>Generated Result:</h4>
            {generatedImages.map((image, index) => (
              <div
                key={index}
                className={`image-result ${selectedImageIndex === index ? "selected" : ""}`}
                onClick={() => setSelectedImageIndex(index)}
              >
                {image.url || image.b64_json ? (
                  <>
                    <img
                      src={
                        image.b64_json 
                          ? `data:image/png;base64,${image.b64_json}`
                          : image.url
                      }
                      alt={image.revised_prompt || `Generated image ${index + 1}`}
                    />
                    <button
                      className="insert-button"
                      onClick={() => handleInsertImage(image)}
                    >
                      Insert to Canvas
                    </button>
                  </>
                ) : (
                  <div className="description-result">
                    <p>
                      <strong>Generated Description:</strong>
                    </p>
                    <p>{image.revised_prompt}</p>
                    <p className="info-text">
                      Note: Image generation requires DALL-E endpoint integration.
                      Currently showing AI-generated description.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default PromptToImageDialog;
